import React, { useState } from 'react';
import { Box, Button, Chip, Divider, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PaymentIcon from '@mui/icons-material/Payment';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  formatBookingCurrency,
  getEnrichedPaymentSummaryData,
  isBookingPaymentClear,
} from '../../utils/bookingPaymentSummary';
import { BOOKING_DRAWER_ACCENT } from '../../utils/bookingDrawerLayout';
import BookingRestaurantPaymentDrawer from './BookingRestaurantPaymentDrawer';

const HOTEL_ACCENT = BOOKING_DRAWER_ACCENT || '#4CE1B6';
const REST_ACCENT = '#14b8a6';

function MoneyRow({
  label,
  value,
  currency,
  subtle,
  text,
  bold = false,
  valueColor,
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.65 }}>
      <Typography
        variant="caption"
        sx={{ color: subtle, fontSize: '0.72rem', fontWeight: bold ? 700 : 500 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 800 : 600,
          fontSize: bold ? '0.85rem' : '0.8rem',
          color: valueColor || text,
          flexShrink: 0,
        }}
      >
        {formatBookingCurrency(value, currency)}
      </Typography>
    </Box>
  );
}

function SectionChip({ icon, label, color, isDark }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.85,
        py: 0.35,
        borderRadius: '999px',
        bgcolor: alpha(color, isDark ? 0.18 : 0.1),
        border: `1px solid ${alpha(color, isDark ? 0.35 : 0.28)}`,
      }}
    >
      {icon}
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.04em', color, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function DueBanner({ label, amount, currency, isDark }) {
  const due = Number(amount) > 0.009;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1,
        mt: 0.25,
        borderRadius: '8px',
        backgroundColor: due
          ? isDark
            ? 'rgba(255, 152, 0, 0.12)'
            : 'rgba(255, 152, 0, 0.06)'
          : isDark
            ? 'rgba(76, 175, 80, 0.12)'
            : 'rgba(76, 175, 80, 0.06)',
        border: `1px solid ${
          due
            ? isDark
              ? 'rgba(255, 152, 0, 0.28)'
              : 'rgba(255, 152, 0, 0.2)'
            : isDark
              ? 'rgba(76, 175, 80, 0.28)'
              : 'rgba(76, 175, 80, 0.2)'
        }`,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.72rem', fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 800,
          fontSize: '0.85rem',
          color: due
            ? isDark
              ? '#ffb74d'
              : '#f57c00'
            : isDark
              ? '#81c784'
              : '#2e7d32',
        }}
      >
        {formatBookingCurrency(amount, currency)}
      </Typography>
    </Box>
  );
}

function TotalsBlock({
  title,
  icon,
  color,
  isDark,
  subtle,
  text,
  currency,
  total,
  paid,
  due,
  headerAction = null,
}) {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.85,
        }}
      >
        <SectionChip icon={icon} label={title} color={color} isDark={isDark} />
        {headerAction}
      </Box>
      <MoneyRow label="Total" value={total} currency={currency} subtle={subtle} text={text} bold />
      <MoneyRow
        label="Paid"
        value={paid}
        currency={currency}
        subtle={subtle}
        text={text}
        valueColor={isDark ? '#81c784' : '#2e7d32'}
      />
      <DueBanner label="Due" amount={due} currency={currency} isDark={isDark} />
    </Box>
  );
}


function PaymentClearBanner({ isDark, drawerTheme, message }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.5,
        p: 1.5,
        backgroundColor: isDark ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
        border: `2px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'}`,
        borderRadius: '12px',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
          }}
        >
          <CheckCircleIcon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            component="div"
            variant="body2"
            sx={{
              fontWeight: 700,
              color: isDark ? '#81c784' : '#2e7d32',
              fontSize: '0.9rem',
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Payment Clear
            <Chip
              label="Complete"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
                color: isDark ? '#81c784' : '#2e7d32',
                border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
              }}
            />
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: drawerTheme?.subtleText || (isDark ? '#999' : '#666'),
              fontSize: '0.8rem',
              lineHeight: 1.5,
              display: 'block',
            }}
          >
            {message}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * Compact payment totals for booking / check-in / check-out / allocation drawers.
 * Hotel Add Payment + Restaurant Collect Payment (POS drawer).
 */
export default function BookingDrawerPaymentCard({
  booking,
  payments = [],
  isDark = false,
  drawerTheme = {},
  accentColor = HOTEL_ACCENT,
  onAddPayment,
  addPaymentLabel = 'Add Payment',
  onRestaurantPaymentsChanged,
  clearMessage = 'All payments have been collected for this booking.',
  showClearBanner = true,
}) {
  const [restaurantPaymentOpen, setRestaurantPaymentOpen] = useState(false);
  const summary = getEnrichedPaymentSummaryData(booking, payments);
  if (!summary) return null;

  const subtle = drawerTheme.subtleText || (isDark ? '#999' : '#666');
  const text = drawerTheme.text || (isDark ? '#ddd' : '#333');
  const currency = summary.currency || 'INR';
  const hasRestaurant = Boolean(summary.hasRestaurant);
  const paymentClear = showClearBanner && isBookingPaymentClear(summary);

  const collectBtnSx = {
    color: REST_ACCENT,
    borderColor: REST_ACCENT,
    fontSize: '0.7rem',
    py: 0.5,
    px: 1.2,
    minHeight: 34,
    height: 34,
    borderRadius: '10px',
    minWidth: 'auto',
    flexShrink: 0,
    '&:hover': {
      borderColor: REST_ACCENT,
      backgroundColor: alpha(REST_ACCENT, isDark ? 0.15 : 0.1),
    },
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: paymentClear ? 0 : 1.5,
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
          borderRadius: '12px',
          backgroundImage: isDark
            ? `linear-gradient(165deg, ${alpha(accentColor, 0.08)} 0%, transparent 55%)`
            : `linear-gradient(165deg, ${alpha(accentColor, 0.06)} 0%, transparent 55%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PaymentIcon sx={{ fontSize: 15, color: accentColor }} />
            <Typography
              variant="caption"
              sx={{ color: subtle, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.02em' }}
            >
              Payment
            </Typography>
          </Box>
          {typeof onAddPayment === 'function' ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddPayment}
              sx={{
                color: accentColor,
                borderColor: accentColor,
                fontSize: '0.7rem',
                py: 0.5,
                px: 1.2,
                minHeight: 34,
                height: 34,
                borderRadius: '10px',
                minWidth: 'auto',
                flexShrink: 0,
                '&:hover': {
                  borderColor: accentColor,
                  backgroundColor: alpha(accentColor, isDark ? 0.15 : 0.1),
                },
              }}
            >
              {addPaymentLabel}
            </Button>
          ) : null}
        </Box>

        <TotalsBlock
          title="Hotel"
          icon={<HotelIcon sx={{ fontSize: 13, color: HOTEL_ACCENT }} />}
          color={HOTEL_ACCENT}
          isDark={isDark}
          subtle={subtle}
          text={text}
          currency={currency}
          total={summary.hotelTotal}
          paid={summary.hotelPaid}
          due={summary.hotelRemaining}
        />

        {hasRestaurant ? (
          <>
            <Divider sx={{ my: 1.35, borderColor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08) }} />
            <TotalsBlock
              title="Restaurant"
              icon={<RestaurantIcon sx={{ fontSize: 13, color: REST_ACCENT }} />}
              color={REST_ACCENT}
              isDark={isDark}
              subtle={subtle}
              text={text}
              currency={currency}
              total={summary.restaurantTotal}
              paid={summary.restaurantPaid}
              due={summary.restaurantRemaining}
              headerAction={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setRestaurantPaymentOpen(true)}
                  sx={collectBtnSx}
                >
                  Collect
                </Button>
              }
            />
          </>
        ) : null}
      </Paper>

      {paymentClear ? (
        <Box sx={{ mb: 1.5 }}>
          <PaymentClearBanner isDark={isDark} drawerTheme={drawerTheme} message={clearMessage} />
        </Box>
      ) : null}

      <BookingRestaurantPaymentDrawer
        open={restaurantPaymentOpen}
        onClose={() => setRestaurantPaymentOpen(false)}
        booking={booking}
        isDark={isDark}
        onPaymentsChanged={async () => {
          await onRestaurantPaymentsChanged?.();
        }}
      />
    </>
  );
}
